import { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Grid, Card, CardContent, CardMedia, useTheme, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';
import { Home as HomeIcon, Business, People, Star } from '@mui/icons-material';
import '../styles/animations.css';
import heroimage from '../assets/hero-image.jpeg';
import { Helmet } from 'react-helmet';

const Home = () => {
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const featuredProjects = [
    {
      id: 1,
      name: '32nd Avenue',
      description: 'Premium commercial project with high-end retail and F&B spaces',
      image: '/images/32nd-avenue.jpg'
    },
    {
      id: 2,
      name: 'DLF Privana',
      description: 'Luxury residential project with 3 & 4 BHK apartments',
      image: '/images/dlf-privana.jpg'
    },
    {
      id: 3,
      name: 'Emaar Gurgaon Greens',
      description: 'Green living spaces with modern amenities in Sector 102',
      image: '/images/emaar-gurgaon-greens.jpg'
    }
  ];

  return (
    <Box>
      <Helmet>
        <title>4 Roots Realty | Luxury Residential & Commercial Property in Gurgaon</title>
        <meta
          name="description"
          content="Explore top luxury flats and commercial real estate projects in Gurgaon – DLF, M3M, Emaar, 32nd Avenue and more. 4 Roots Realty offers trusted property services in Gurgaon & Chandigarh."
        />
        <meta
          name="keywords"
          content="Real Estate Gurgaon, Luxury Flats Gurgaon, Commercial Projects Gurgaon, DLF Gurgaon, Emaar Sector 102, M3M Golf Course Road, Sector 77 Gurgaon, New Launch Projects Gurgaon, Property Dealer Gurgaon"
        />
        <link rel="canonical" href="https://www.4rootsrealty.com/" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            name: "4 Roots Realty",
            url: "https://www.4rootsrealty.com",
            logo: "https://www.4rootsrealty.com/logo.png",
            description:
              "4 Roots Realty offers luxury residential and commercial property solutions in Gurgaon & Chandigarh. Featuring top projects from DLF, M3M, Emaar and more.",
            areaServed: {
              "@type": "Place",
              name: "Gurgaon, Haryana"
            },
            contactPoint: {
              "@type": "ContactPoint",
              telephone: "+91-7837664439",
              contactType: "Sales"
            }
          })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <Box 
        sx={{ 
          bgcolor: 'primary.light', 
          color: 'white',
          minHeight: { xs: '90vh', sm: '80vh', md: '70vh' },
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          transition: 'all 0.3s ease-in-out',
          transform: scrolled ? 'scale(0.98)' : 'scale(1)',
          boxShadow: scrolled ? '0 10px 30px rgba(0,0,0,0.1)' : 'none',
        }}
        className="animate-slideInFromTop"
      >
        <Container 
          maxWidth="xl" 
          sx={{ 
            position: 'relative', 
            zIndex: 2,
            transition: 'all 0.3s ease-in-out',
            transform: scrolled ? 'translateY(-20px)' : 'translateY(0)',
            px: { xs: 2, sm: 3, md: 4 }
          }}
        >
          <Grid container alignItems="center" spacing={4}>
            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  pr: { md: 8 },
                  textAlign: { xs: 'center', md: 'left' },
                  transition: 'all 0.3s ease-in-out',
                  opacity: scrolled ? 0.9 : 1,
                }}
              >
                <Typography 
                  variant="h1" 
                  className="animate-slideInFromTop"
                  sx={{ 
                    fontSize: { 
                      xs: '2rem',
                      sm: '2.5rem', 
                      md: '3rem', 
                      lg: '3.5rem' 
                    },
                    mb: { xs: 1.5, md: 2 },
                    fontWeight: 'bold',
                    lineHeight: 1.2
                  }}
                >
                  Welcome to 4 Roots Realty
                </Typography>
                <Typography 
                  variant="h5" 
                  className="animate-fadeInUp"
                  sx={{ 
                    mb: { xs: 3, md: 4 }, 
                    opacity: 0.9,
                    fontSize: { 
                      xs: '0.9rem',
                      sm: '1rem', 
                      md: '1.1rem' 
                    },
                    lineHeight: 1.6,
                    px: { xs: 2, md: 0 }
                  }}
                >
                  Your trusted partner in finding the perfect property
                </Typography>
                <Button 
                  variant="contained" 
                  component={Link}
                  to="/contact"
                  size={isMobile ? "medium" : "large"}
                  className="animate-fadeInUp"
                  sx={{ 
                    bgcolor: 'white', 
                    color: 'primary.main',
                    px: { xs: 2.5, md: 3 },
                    py: { xs: 1, md: 1.2 },
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.15)'
                    },
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
        
        {/* Hero Image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
            background: theme => theme.palette.primary.light,
            transition: 'all 0.3s ease-in-out',
            transform: scrolled ? 'scale(1.02)' : 'scale(1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: theme => `linear-gradient(
                ${isMobile ? '180deg' : 'to right'}, 
                ${theme.palette.primary.main}dd 0%,
                ${theme.palette.primary.main}bb 30%,
                ${theme.palette.primary.main}88 60%,
                ${theme.palette.primary.main}44 100%
              )`,
              zIndex: 2,
              pointerEvents: 'none',
              transition: 'all 0.3s ease-in-out',
              opacity: scrolled ? 0.8 : 1,
            }
          }}
        >
          <Box
            component="img"
            src={heroimage}
            alt="Luxury Home"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: isMobile ? 'center' : 'right center',
              opacity: 0.9,
              transition: 'all 0.3s ease-in-out',
              transform: scrolled ? 'scale(1.05)' : 'scale(1)',
            }}
          />
        </Box>
      </Box>

      {/* Features Section */}
      <Container sx={{ 
        py: { xs: 6, md: 8 },
        px: { xs: 2, sm: 3, md: 4 }
      }}>
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {[
            {
              icon: <HomeIcon sx={{ fontSize: { xs: 32, md: 40 } }}/>,
              title: 'Premium Properties',
              description: 'Discover handpicked luxury properties in prime locations.'
            },
            {
              icon: <Business sx={{ fontSize: { xs: 32, md: 40 } }}/>,
              title: 'Expert Guidance',
              description: 'Get professional advice from our experienced real estate team.'
            },
            {
              icon: <People sx={{ fontSize: { xs: 32, md: 40 } }}/>,
              title: 'Personal Approach',
              description: 'We tailor our services to meet your specific needs.'
            },
            {
              icon: <Star sx={{ fontSize: { xs: 32, md: 40 } }}/>,
              title: 'Trusted Service',
              description: 'Join our growing family of satisfied clients.'
            }
          ].map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card 
                className={`animate-slideInFromRight hover-scale delay-${index * 100}`}
                sx={{ 
                  height: '100%',
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  boxShadow: 2,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)'
                  }
                }}
              >
                <CardContent sx={{ 
                  textAlign: 'center', 
                  p: { xs: 2, md: 3 }
                }}>
                  <Box sx={{ 
                    color: 'primary.main', 
                    mb: { xs: 1.5, md: 2 }
                  }}>
                    {feature.icon}
                  </Box>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      mb: 1,
                      fontSize: { xs: '1.1rem', md: '1.25rem' }
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography 
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Featured Projects */}
      <Container sx={{ 
        py: { xs: 6, md: 8 },
        px: { xs: 2, sm: 3, md: 4 }
      }}>
        <Typography 
          variant="h3" 
          align="center" 
          gutterBottom
          sx={{
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
            mb: { xs: 3, md: 4 }
          }}
        >
          Featured Projects
        </Typography>
        <Grid container spacing={{ xs: 2, md: 4 }} sx={{ mt: { xs: 2, md: 4 } }}>
          {featuredProjects.map(project => (
            <Grid item xs={12} sm={6} md={4} key={project.id}>
              <Card 
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)'
                  }
                }}
              >
                <CardMedia
                  component="img"
                  height={isMobile ? "200" : "240"}
                  image={project.image}
                  alt={project.name}
                  sx={{
                    objectFit: 'cover'
                  }}
                />
                <CardContent sx={{ flexGrow: 1, p: { xs: 2, md: 3 } }}>
                  <Typography 
                    variant="h5" 
                    gutterBottom
                    sx={{
                      fontSize: { xs: '1.25rem', md: '1.5rem' }
                    }}
                  >
                    {project.name}
                  </Typography>
                  <Typography 
                    variant="body1"
                    color="text.secondary"
                    sx={{
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }}
                  >
                    {project.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Google Form Section */}
      <Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
        <Container>
          <Typography variant="h3" align="center" gutterBottom>
            Get in Touch
          </Typography>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <iframe
              src="https://docs.google.com/forms/d/e/YOUR_FORM_ID/viewform?embedded=true"
              width="100%"
              height="600"
              frameBorder="0"
              marginHeight={0}
              marginWidth={0}
            >
              Loading…
            </iframe>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 